// === Git Panel & Git Commands ===
// Extracted from app.ts — git state, panel rendering, commands, modals, resize

import { escapeHtml } from '../utils';
import { send } from '../ws/connection';
import { waitForWsEvent } from '../ws/events';
import { showToast } from './toast';
import { gitState, sessionState } from '../state';

// --- DI callbacks (injected by app.ts via setupGitHandlers) ---
let _appendError: ((msg: string, opts?: any) => void) | null = null;
let _createOverlayPanel: ((opts: any) => any) | null = null;
let _showOptionPicker: ((title: string, opts: any[], current: string, cb: (v: string) => void) => void) | null = null;
let _renderWorkspaceInsights: (() => void) | null = null;
let _buildWorkspaceActionButtons: ((actions: any[], options?: any) => string) | null = null;
let _nextLocalId: ((prefix?: string) => string) | null = null;

export function setupGitHandlers(handlers: {
  appendError: (msg: string, opts?: any) => void;
  createOverlayPanel: (opts: any) => any;
  showOptionPicker: (title: string, opts: any[], current: string, cb: (v: string) => void) => void;
  renderWorkspaceInsights: () => void;
  buildWorkspaceActionButtons: (actions: any[], options?: any) => string;
  nextLocalId: (prefix?: string) => string;
}): void {
  _appendError = handlers.appendError;
  _createOverlayPanel = handlers.createOverlayPanel;
  _showOptionPicker = handlers.showOptionPicker;
  _renderWorkspaceInsights = handlers.renderWorkspaceInsights;
  _buildWorkspaceActionButtons = handlers.buildWorkspaceActionButtons;
  _nextLocalId = handlers.nextLocalId;
}

// --- Git panel resize state (module-level, mirrors app.ts gitPanelResizeState) ---
let gitPanelResizeState: any = null;

// --- Constants ---
const GIT_PANEL_WIDTH_STORAGE_KEY = 'webcoding-git-panel-width';
const GIT_PANEL_DEFAULT_WIDTH = 360;
const GIT_PANEL_MIN_WIDTH = 280;
const GIT_PANEL_MAX_WIDTH = 720;
const DESKTOP_INSIGHTS_BREAKPOINT = 1280;

// --- DOM helpers (lazy) ---
function getGitPanelEl(): Element | null { return document.getElementById('git-panel'); }
function getGitPanelContent(): Element | null { return document.getElementById('git-panel-content'); }
function getGitPanelBtn(): Element | null { return document.getElementById('git-panel-btn'); }
function getWorkspaceMain(): Element | null { return document.querySelector('.workspace-main'); }
function getWorkspaceInsights(): Element | null { return document.getElementById('workspace-insights'); }

// ============================================================
// Git panel resize helpers
// ============================================================

export function ensureGitPanelResizer(): Element | null {
  const gitPanelEl = getGitPanelEl();
  if (!gitPanelEl?.parentNode) return null;
  const existing = document.getElementById('git-panel-resizer');
  if (existing) return existing;
  const resizer = document.createElement('div');
  resizer.id = 'git-panel-resizer';
  resizer.className = 'git-panel-resizer';
  resizer.setAttribute('aria-hidden', 'true');
  gitPanelEl.parentNode.insertBefore(resizer, gitPanelEl);
  return resizer;
}

function getGitPanelWidthLimit(): number {
  const workspaceMain = getWorkspaceMain();
  const workspaceInsights = getWorkspaceInsights();
  const containerWidth = workspaceMain?.getBoundingClientRect().width || window.innerWidth || GIT_PANEL_DEFAULT_WIDTH;
  const insightsWidth = window.matchMedia(`(min-width: ${DESKTOP_INSIGHTS_BREAKPOINT}px)`).matches
    ? (workspaceInsights?.getBoundingClientRect().width || 0)
    : 0;
  return Math.min(GIT_PANEL_MAX_WIDTH, Math.max(GIT_PANEL_MIN_WIDTH, containerWidth - insightsWidth - 320));
}

function clampGitPanelWidth(width: number): number {
  const numericWidth = Number(width);
  if (!Number.isFinite(numericWidth)) return GIT_PANEL_DEFAULT_WIDTH;
  return Math.max(GIT_PANEL_MIN_WIDTH, Math.min(getGitPanelWidthLimit(), Math.round(numericWidth)));
}

function applyGitPanelWidth(width: number, options: any = {}): number {
  const nextWidth = clampGitPanelWidth(width);
  document.documentElement.style.setProperty('--git-panel-width', `${nextWidth}px`);
  if (!options.skipPersist) {
    localStorage.setItem(GIT_PANEL_WIDTH_STORAGE_KEY, String(nextWidth));
  }
  return nextWidth;
}

export function canResizeGitPanel(): boolean {
  return !!getGitPanelEl() && !!ensureGitPanelResizer() && window.matchMedia('(min-width: 769px) and (pointer: fine)').matches;
}

function syncGitPanelResizerVisibility(): void {
  const resizer = ensureGitPanelResizer();
  if (!resizer) return;
  resizer.classList.toggle('visible', gitState.panelOpen && canResizeGitPanel());
}

export function syncGitPanelWidthForViewport(): void {
  syncGitPanelResizerVisibility();
  if (!canResizeGitPanel()) {
    document.body.classList.remove('git-panel-resizing');
    gitPanelResizeState = null;
    document.documentElement.style.setProperty('--git-panel-width', `${GIT_PANEL_DEFAULT_WIDTH}px`);
    return;
  }
  const savedWidth = parseInt(localStorage.getItem(GIT_PANEL_WIDTH_STORAGE_KEY) || `${GIT_PANEL_DEFAULT_WIDTH}`, 10);
  applyGitPanelWidth(savedWidth, { skipPersist: true });
}

export function handleGitPanelResizeStart(e: PointerEvent): void {
  const gitPanelEl = getGitPanelEl();
  if (!gitState.panelOpen || !canResizeGitPanel()) return;
  if (typeof e.button === 'number' && e.button !== 0) return;
  gitPanelResizeState = {
    startX: e.clientX,
    startWidth: gitPanelEl!.getBoundingClientRect().width,
  };
  document.body.classList.add('git-panel-resizing');
  const resizer = ensureGitPanelResizer();
  if (typeof resizer?.setPointerCapture === 'function' && e.pointerId !== undefined) {
    resizer.setPointerCapture(e.pointerId);
  }
  e.preventDefault();
}

export function handleGitPanelResizeMove(e: PointerEvent): void {
  if (!gitPanelResizeState) return;
  const deltaX = gitPanelResizeState.startX - e.clientX;
  applyGitPanelWidth(gitPanelResizeState.startWidth + deltaX, { skipPersist: true });
}

export function handleGitPanelResizeEnd(e?: PointerEvent): void {
  if (!gitPanelResizeState) return;
  const releasedPointerId = e?.pointerId;
  const resizer = ensureGitPanelResizer();
  if (typeof resizer?.releasePointerCapture === 'function' && releasedPointerId !== undefined) {
    try { resizer.releasePointerCapture(releasedPointerId); } catch {}
  }
  const gitPanelEl = getGitPanelEl();
  const currentWidth = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--git-panel-width'), 10)
    || (gitPanelEl as HTMLElement)?.getBoundingClientRect().width
    || GIT_PANEL_DEFAULT_WIDTH;
  applyGitPanelWidth(currentWidth);
  gitPanelResizeState = null;
  document.body.classList.remove('git-panel-resizing');
}

// ============================================================
// Git state
// ============================================================

export function resetGitState(next: any = {}): void {
  Object.assign(gitState, {
    cwd: null,
    loading: false,
    lastError: '',
    status: null,
    logEntries: [],
    branchEntries: [],
    activePanelView: 'status',
    panelDiffContent: '',
    panelDiffTitle: '',
    panelLogEntries: [],
    filesExpanded: false,
    collapsedTreeNodes: [],
    ...next,
  });
  updateGitPanelBadge();
  if (gitState.panelOpen) renderGitPanel();
}

export function getGitRepoLabel(repoRoot: string): string {
  const normalized = String(repoRoot || '').replace(/[\\/]+$/, '');
  if (!normalized) return '未识别仓库';
  const parts = normalized.split(/[\\/]/).filter(Boolean);
  return parts[parts.length - 1] || normalized;
}

// ============================================================
// Git commands
// ============================================================

export function sendGitCommand(action: string, params: any = {}, options: any = {}): Promise<any> {
  const cwd = String(options.cwd || params.cwd || sessionState.currentCwd || '').trim();
  if (!cwd) {
    return Promise.reject(new Error('当前会话还没有工作目录，无法执行 Git 操作。'));
  }
  const requestId = _nextLocalId!('git');
  const waiter = waitForWsEvent('git_result', {
    timeoutMs: Number.isFinite(options.timeoutMs) ? options.timeoutMs : 15000,
    predicate: (payload: any) => payload?.requestId === requestId && payload?.action === action,
  });
  if (action === 'status') {
    gitState.loading = true;
    gitState.lastError = '';
    gitState.cwd = cwd;
    _renderWorkspaceInsights?.();
  }
  send({
    ...params,
    type: 'git_command',
    action,
    cwd,
    sessionId: sessionState.currentSessionId,
    requestId,
  });
  return waiter.then((payload: any) => {
    if (!payload?.success) throw new Error(payload?.error || 'Git 操作失败');
    return payload;
  });
}

export function requestGitStatus(options: any = {}): Promise<any> {
  if (!sessionState.currentCwd) {
    resetGitState();
    _renderWorkspaceInsights?.();
    return Promise.resolve(null);
  }
  return sendGitCommand('status', {}, options).catch((error: any) => {
    gitState.loading = false;
    gitState.status = null;
    gitState.lastError = error.message || '读取 Git 状态失败';
    _renderWorkspaceInsights?.();
    if (!options.silent) _appendError?.(gitState.lastError);
    return null;
  });
}

// ============================================================
// Git file display helpers
// ============================================================

function gitFileStatusText(file: any): string {
  if (!file) return '未知状态';
  if (file.conflicted) return '冲突';
  if (file.untracked) return '未跟踪';
  if (file.renamed) return '已重命名';
  if (file.deleted && file.staged) return '已暂存删除';
  if (file.deleted) return '已删除';
  if (file.added && file.staged) return '已暂存新增';
  if (file.added) return '新增';
  if (file.staged && file.modified) return '已暂存 + 未暂存';
  if (file.staged) return '已暂存';
  if (file.modified) return '已修改';
  return file.code || '已变更';
}

function buildGitFileRowsMarkup(files: any[], expanded = false): string {
  const list = Array.isArray(files) ? files : [];
  if (list.length === 0) {
    return '<div class="insights-note">工作区干净，没有未提交改动。</div>';
  }
  const visibleFiles = expanded ? list : list.slice(0, 6);
  const hiddenCount = list.length - visibleFiles.length;
  return `
    <div style="display:flex;flex-direction:column;gap:4px;margin-top:8px">
      ${visibleFiles.map((file) => `
        <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;border-top:1px solid var(--line);padding-top:4px">
          <div style="min-width:0;flex:1">
            <div style="font-size:12px;font-weight:600;color:var(--text-primary);word-break:break-all;line-height:1.3">${escapeHtml(file.path)}</div>
            <div style="font-size:11px;color:var(--text-secondary);line-height:1.2">${escapeHtml(gitFileStatusText(file))}</div>
          </div>
          <div style="display:flex;gap:4px;flex-shrink:0">
            <button
              class="workspace-action-btn"
              type="button"
              style="padding:2px 2px;font-size:10px;color:var(--text-muted);border-color:var(--line);box-shadow:none;background:transparent;min-width:0;letter-spacing:0"
              data-git-action="show-diff"
              data-git-file="${escapeHtml(file.path)}"
              ${file.staged && !file.modified ? 'data-git-staged="1"' : ''}
            >diff</button>
            ${!file.staged ? `
              <button
                class="workspace-action-btn"
                type="button"
                style="padding:3px 7px;font-size:11px"
                data-git-action="add"
                data-git-file="${escapeHtml(file.path)}"
              >暂存</button>
            ` : ''}
          </div>
        </div>
      `).join('')}
    </div>
    ${hiddenCount > 0 ? `
      <button class="insights-note" style="cursor:pointer;text-decoration:underline;text-underline-offset:3px;background:none;border:none;padding:0;color:var(--text-muted);font-size:inherit;width:100%;text-align:left" data-git-action="expand-files">还有 ${hiddenCount} 个文件，点击展开全部</button>
    ` : ''}
  `;
}

function normalizeGitTreePath(path: string): string {
  return String(path || '').replace(/\\/g, '/').replace(/^\.\//, '').replace(/^\//, '');
}

function createEmptyGitTreeSummary(): any {
  return { total: 0, staged: 0, modified: 0, untracked: 0, conflicted: 0 };
}

function appendGitFileToSummary(summary: any, file: any): any {
  if (!summary || !file) return summary;
  summary.total += 1;
  if (file.staged) summary.staged += 1;
  if (file.modified) summary.modified += 1;
  if (file.untracked) summary.untracked += 1;
  if (file.conflicted) summary.conflicted += 1;
  return summary;
}

function buildGitTreeBadges(summary: any): string {
  const badges = [
    summary?.staged ? { label: `暂存 ${summary.staged}`, tone: 'staged' } : null,
    summary?.modified ? { label: `修改 ${summary.modified}`, tone: 'modified' } : null,
    summary?.untracked ? { label: `未跟踪 ${summary.untracked}`, tone: 'untracked' } : null,
    summary?.conflicted ? { label: `冲突 ${summary.conflicted}`, tone: 'conflicted' } : null,
  ].filter(Boolean);
  if (!badges.length) return '';
  return `
    <span class="git-tree-badges">
      ${badges.map((badge: any) => `<span class="git-tree-badge ${badge.tone}">${escapeHtml(badge.label)}</span>`).join('')}
    </span>
  `;
}

function gitFileStatusTone(file: any): string {
  if (!file) return '';
  if (file.conflicted) return 'conflicted';
  if (file.modified) return 'modified';
  if (file.staged) return 'staged';
  if (file.untracked) return 'untracked';
  return '';
}

function buildGitFileTree(files: any[]): any[] {
  const root: any[] = [];
  const folders = new Map<string, any>();
  const list = Array.isArray(files) ? files : [];
  const ensureFolder = (segments: string[]): any => {
    const key = segments.join('/');
    if (folders.has(key)) return folders.get(key);
    const node = {
      type: 'folder',
      name: segments[segments.length - 1] || '',
      key,
      children: [],
      summary: createEmptyGitTreeSummary(),
    };
    if (segments.length === 1) {
      root.push(node);
    } else {
      ensureFolder(segments.slice(0, -1)).children.push(node);
    }
    folders.set(key, node);
    return node;
  };

  for (const file of list) {
    const normalizedPath = normalizeGitTreePath(file?.path);
    if (!normalizedPath) continue;
    const nextFile = { ...file, path: normalizedPath };
    const segments = normalizedPath.split('/').filter(Boolean);
    if (segments.length <= 1) {
      root.push({ type: 'file', key: normalizedPath, file: nextFile });
      continue;
    }
    const folderSegments = segments.slice(0, -1);
    folderSegments.forEach((_, index) => {
      appendGitFileToSummary(ensureFolder(folderSegments.slice(0, index + 1)).summary, nextFile);
    });
    ensureFolder(folderSegments).children.push({ type: 'file', key: normalizedPath, file: nextFile });
  }

  const sortNodes = (nodes: any[]) => {
    nodes.sort((a: any, b: any) => {
      if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
      const aName = a.type === 'folder'
        ? a.name
        : (a.file?.path.split('/').pop() || a.file?.path || '');
      const bName = b.type === 'folder'
        ? b.name
        : (b.file?.path.split('/').pop() || b.file?.path || '');
      return aName.localeCompare(bName, 'zh-CN', { numeric: true, sensitivity: 'base' });
    });
    nodes.forEach((node: any) => {
      if (node.type === 'folder') sortNodes(node.children);
    });
  };

  sortNodes(root);
  return root;
}

function renderGitTreeNodes(nodes: any[], collapsedSet: Set<string>, depth = 0): string {
  return nodes.map((node) => {
    if (node.type === 'folder') {
      const isCollapsed = collapsedSet.has(node.key);
      return `
        <div class="git-tree-node git-tree-folder${isCollapsed ? ' collapsed' : ''}" style="--git-tree-depth:${depth}">
          <button
            type="button"
            class="git-tree-folder-header"
            data-git-action="toggle-tree-node"
            data-git-node="${escapeHtml(node.key)}"
            aria-expanded="${String(!isCollapsed)}"
          >
            <span class="git-tree-folder-leading">
              <span class="git-tree-folder-rail" aria-hidden="true">
                <span class="git-tree-chevron">▸</span>
              </span>
              <span class="git-tree-folder-main">
                <span class="git-tree-folder-name">${escapeHtml(node.name)}</span>
                <span class="git-tree-folder-meta">${node.summary.total} 个变更文件</span>
              </span>
            </span>
            <span class="git-tree-folder-trailing">
              <span class="git-tree-folder-badges">
                ${buildGitTreeBadges(node.summary)}
              </span>
            </span>
          </button>
          <div class="git-tree-children${isCollapsed ? ' collapsed' : ''}">
            ${renderGitTreeNodes(node.children, collapsedSet, depth + 1)}
          </div>
        </div>
      `;
    }
    const file = node.file;
    const statusText = gitFileStatusText(file);
    const statusTone = gitFileStatusTone(file);
    return `
      <div class="git-tree-node git-tree-file" style="--git-tree-depth:${depth}">
        <div class="git-tree-file-row">
          <div class="git-tree-file-leading" aria-hidden="true">
            <span class="git-tree-file-marker"></span>
          </div>
          <div class="git-tree-file-main">
            <div class="git-tree-file-head">
              <div class="git-tree-file-name">${escapeHtml(file.path.split('/').pop() || file.path)}</div>
              <span class="git-tree-badge git-tree-file-status${statusTone ? ` ${statusTone}` : ''}">${escapeHtml(statusText)}</span>
            </div>
            <div class="git-tree-file-meta">${escapeHtml(file.path)}</div>
          </div>
          <div class="git-tree-file-trailing">
            <div class="git-tree-file-actions">
              <button
                class="workspace-action-btn git-tree-action-btn"
                type="button"
                data-git-action="show-diff"
                data-git-file="${escapeHtml(file.path)}"
                ${file.staged && !file.modified ? 'data-git-staged="1"' : ''}
              >diff</button>
              ${!file.staged ? `
                <button
                  class="workspace-action-btn git-tree-action-btn git-tree-stage-btn"
                  type="button"
                  data-git-action="add"
                  data-git-file="${escapeHtml(file.path)}"
                >暂存</button>
              ` : ''}
            </div>
          </div>
        </div>
      </div>
    `;
  }).join('');
}

function buildGitFileTreeMarkup(files: any[]): string {
  const list = Array.isArray(files) ? files : [];
  if (list.length === 0) {
    return '<div class="insights-note">工作区干净，没有未提交改动。</div>';
  }
  const collapsedSet = new Set<string>(Array.isArray(gitState.collapsedTreeNodes) ? gitState.collapsedTreeNodes : []);
  const tree = buildGitFileTree(list);
  return `<div class="git-tree-list">${renderGitTreeNodes(tree, collapsedSet)}</div>`;
}

// ============================================================
// Git insights card markup (used by workspace insights panel)
// ============================================================

export function buildGitInsightsCardMarkup(): string {
  const cwd = sessionState.currentCwd || '';
  const status = gitState.status;
  const summary = status?.summary || { staged: 0, modified: 0, untracked: 0, conflicted: 0 };
  const repoName = status?.repoRoot ? getGitRepoLabel(status.repoRoot) : (cwd ? getGitRepoLabel(cwd) : '未识别仓库');
  const trackingParts: string[] = [];
  if (status?.upstream) trackingParts.push(status.upstream);
  const stateLabel = !cwd
    ? '未就绪'
    : gitState.loading
      ? '刷新中'
      : status
        ? (status.clean ? '干净' : '有改动')
        : '未加载';
  const actionButtons = cwd ? _buildWorkspaceActionButtons!([
    { action: 'refresh-git', label: '刷新 Git', primary: true },
    { action: 'show-git-diff', label: '工作区 Diff' },
    { action: 'show-git-staged-diff', label: '已暂存 Diff' },
    { action: 'show-git-log', label: '查看 Log' },
    { action: 'git-add-all', label: '全部暂存' },
    { action: 'git-commit', label: '提交' },
    { action: 'git-checkout', label: '切分支' },
    { action: 'git-create-branch', label: '新建分支' },
  ], { compact: true }) : '';

  let bodyHtml = '';
  if (!cwd) {
    bodyHtml = '<div class="insights-note">当前会话没有工作目录，暂时无法读取 Git 信息。</div>';
  } else if (!status && gitState.loading) {
    bodyHtml = '<div class="insights-note">正在读取 Git 状态…</div>';
  } else if (!status && gitState.lastError) {
    bodyHtml = `<div class="insights-note">${escapeHtml(gitState.lastError)}</div>`;
  } else if (!status) {
    bodyHtml = '<div class="insights-note">点击"刷新 Git"后读取当前仓库状态。</div>';
  } else {
    const statsBadges = [
      summary.staged     ? `<span class="git-insights-badge staged">暂存 ${summary.staged}</span>` : '',
      summary.modified   ? `<span class="git-insights-badge modified">修改 ${summary.modified}</span>` : '',
      summary.untracked  ? `<span class="git-insights-badge untracked">未跟踪 ${summary.untracked}</span>` : '',
      summary.conflicted ? `<span class="git-insights-badge conflicted">冲突 ${summary.conflicted}</span>` : '',
    ].filter(Boolean).join('');
    const hasChanges = statsBadges.length > 0;
    bodyHtml = `
      <div class="git-insights-summary">
        ${trackingParts.length ? `<span class="git-insights-track">${escapeHtml(trackingParts.join(' · '))}</span>` : ''}
        <span class="git-insights-badges">
          ${hasChanges ? statsBadges : '<span class="git-insights-clean">干净</span>'}
        </span>
      </div>
      ${gitState.loading ? '<div class="insights-note">正在刷新 Git 状态…</div>' : ''}
      ${gitState.lastError ? `<div class="insights-note">${escapeHtml(gitState.lastError)}</div>` : ''}
      ${status.files?.length ? buildGitFileRowsMarkup(status.files, gitState.filesExpanded) : ''}
    `;
  }

  const branchLabel = status ? (status.branch || 'HEAD') : '';
  return `
    <section class="insights-card">
      <div class="insights-card-header">
        <div class="git-insights-header-main">
          <h3>${escapeHtml(repoName)}</h3>
          ${branchLabel ? `<span class="git-insights-branch">${escapeHtml(branchLabel)}</span>` : ''}
        </div>
        <span class="insights-status-pill${status && !status.clean ? ' running' : ''}">${escapeHtml(stateLabel)}</span>
      </div>
      ${bodyHtml}
      ${actionButtons}
    </section>
  `;
}

// ============================================================
// Git panel
// ============================================================

export function toggleGitPanel(): void {
  if (gitState.panelOpen) {
    closeGitPanel();
  } else {
    openGitPanel(gitState.activePanelView || 'status');
  }
}

export function openGitPanel(view: string): void {
  const gitPanelEl = getGitPanelEl();
  const gitPanelBtn = getGitPanelBtn();
  gitState.panelOpen = true;
  gitState.activePanelView = view || 'status';
  if (gitPanelEl) gitPanelEl.classList.add('visible');
  syncGitPanelWidthForViewport();
  if (gitPanelBtn) gitPanelBtn.classList.add('active');
  renderGitPanel();
  if (view === 'status' && sessionState.currentCwd && !gitState.status && !gitState.loading) {
    requestGitStatus({ silent: true });
  }
}

export function closeGitPanel(): void {
  const gitPanelEl = getGitPanelEl();
  const gitPanelBtn = getGitPanelBtn();
  handleGitPanelResizeEnd();
  gitState.panelOpen = false;
  if (gitPanelEl) gitPanelEl.classList.remove('visible');
  if (gitPanelBtn) gitPanelBtn.classList.remove('active');
  syncGitPanelWidthForViewport();
}

export function renderGitPanel(): void {
  const gitPanelContent = getGitPanelContent();
  if (!gitPanelContent) return;
  const view = gitState.activePanelView;
  // Sync toolbar disabled state
  const toolbar = document.getElementById('git-panel-toolbar');
  if (toolbar) {
    const disabled = !sessionState.currentCwd;
    toolbar.querySelectorAll('.git-toolbar-btn').forEach((btn: any) => {
      btn.disabled = disabled;
    });
  }
  // Sync tab active state
  const activeTabView = view === 'staged-diff' ? 'diff' : view;
  document.querySelectorAll('.git-panel-tab').forEach((tab: any) => {
    tab.classList.toggle('active', tab.dataset.gitView === activeTabView);
  });
  if (view === 'status') {
    gitPanelContent.innerHTML = buildGitPanelStatusMarkup();
  } else if (view === 'diff' || view === 'staged-diff') {
    gitPanelContent.innerHTML = buildGitPanelDiffMarkup();
  } else if (view === 'log') {
    gitPanelContent.innerHTML = buildGitPanelLogMarkup();
  } else if (view === 'commit') {
    gitPanelContent.innerHTML = buildGitPanelCommitMarkup();
    setupGitPanelCommitHandlers();
  } else {
    gitPanelContent.innerHTML = buildGitPanelStatusMarkup();
  }
}

function buildGitPanelStatusMarkup(): string {
  const cwd = sessionState.currentCwd || '';
  const status = gitState.status;
  const summary = status?.summary || { staged: 0, modified: 0, untracked: 0, conflicted: 0 };
  const trackingParts: string[] = [];
  if (status?.upstream) trackingParts.push(status.upstream);
  if (status?.ahead) trackingParts.push(`ahead ${status.ahead}`);
  if (status?.behind) trackingParts.push(`behind ${status.behind}`);
  let bodyHtml = '';
  if (!cwd) {
    bodyHtml = '<div class="insights-note">当前会话没有工作目录。</div>';
  } else if (!status && gitState.loading) {
    bodyHtml = '<div class="insights-note">正在读取 Git 状态…</div>';
  } else if (!status && gitState.lastError) {
    bodyHtml = `<div class="insights-note">${escapeHtml(gitState.lastError)}</div>`;
  } else if (!status) {
    bodyHtml = '<div class="insights-note">点击「刷新」后读取当前仓库状态。</div>';
  } else {
    bodyHtml = `
      <section class="git-panel-status-shell">
        <div class="git-panel-status-summary">
          <div class="git-panel-status-main">
            <span class="git-panel-status-branch">${escapeHtml(status.branch || 'HEAD')}</span>
            ${trackingParts.length ? `<span class="git-panel-status-track">${escapeHtml(trackingParts.join(' · '))}</span>` : ''}
          </div>
          <div class="git-panel-status-badges">
            ${summary.staged ? `<span class="git-insights-badge staged">暂存 ${summary.staged}</span>` : ''}
            ${summary.modified ? `<span class="git-insights-badge modified">修改 ${summary.modified}</span>` : ''}
            ${summary.untracked ? `<span class="git-insights-badge untracked">未跟踪 ${summary.untracked}</span>` : ''}
            ${summary.conflicted ? `<span class="git-insights-badge conflicted">冲突 ${summary.conflicted}</span>` : ''}
            ${!summary.staged && !summary.modified && !summary.untracked && !summary.conflicted ? '<span class="git-insights-clean">干净</span>' : ''}
          </div>
        </div>
        ${gitState.lastError ? `<div class="insights-note">${escapeHtml(gitState.lastError)}</div>` : ''}
        <div class="git-panel-tree-wrap">
          ${buildGitFileTreeMarkup(status.files || [])}
        </div>
      </section>
    `;
  }
  return bodyHtml;
}

function buildGitPanelDiffMarkup(): string {
  const content = gitState.panelDiffContent;
  const title = gitState.panelDiffTitle;
  if (!content) {
    return '<div class="insights-note">当前没有 diff 内容。</div>';
  }
  return `
    ${title ? `<div style="font-size:12px;color:var(--text-muted);margin-bottom:8px;font-family:var(--font-mono);text-transform:uppercase;letter-spacing:.04em">${escapeHtml(title)}</div>` : ''}
    <pre class="git-panel-code"><code class="language-diff">${escapeHtml(content)}</code></pre>
  `;
}

function buildGitPanelLogMarkup(): string {
  const entries = gitState.panelLogEntries;
  if (!entries.length) {
    return '<div class="insights-note">暂无提交记录。</div>';
  }
  const items = entries.map((e: any) => `
    <div class="git-panel-log-item">
      <span class="git-panel-log-hash">${escapeHtml(e.hash || '')}</span>
      <span class="git-panel-log-subject">${escapeHtml(e.subject || '')}</span>
    </div>
  `).join('');
  return `<div class="git-panel-log-list">${items}</div>`;
}

function buildGitPanelCommitMarkup(): string {
  const stagedCount = gitState.status?.summary?.staged || 0;
  return `
    <div class="git-panel-commit-area">
      <label class="modal-field-label" for="git-panel-commit-message">提交说明</label>
      <textarea id="git-panel-commit-message" class="themed-textarea" rows="6"
        placeholder="例如：feat: add git panel"
        style="min-height:120px;resize:vertical;width:100%"></textarea>
      <div class="settings-inline-note">${stagedCount > 0 ? `当前有 ${stagedCount} 个已暂存文件。` : '当前没有已暂存文件，请先暂存后再提交。'}</div>
      <div class="settings-status" id="git-panel-commit-status"></div>
      <button class="settings-btn primary" id="git-panel-commit-submit"${stagedCount > 0 ? '' : ' disabled'}>提交</button>
    </div>
  `;
}

function setupGitPanelCommitHandlers(): void {
  const submitBtn = document.getElementById('git-panel-commit-submit');
  const messageEl = document.getElementById('git-panel-commit-message') as HTMLTextAreaElement | null;
  const statusEl = document.getElementById('git-panel-commit-status');
  if (!submitBtn || !messageEl) return;
  submitBtn.addEventListener('click', () => {
    const message = String(messageEl.value || '').trim();
    if (!message) {
      if (statusEl) { statusEl.textContent = '提交说明不能为空。'; statusEl.className = 'settings-status error'; }
      return;
    }
    (submitBtn as HTMLButtonElement).disabled = true;
    if (statusEl) { statusEl.textContent = '正在提交…'; statusEl.className = 'settings-status'; }
    sendGitCommand('commit', { message }).then(() => {
      if (statusEl) { statusEl.textContent = '提交完成'; statusEl.className = 'settings-status success'; }
      messageEl.value = '';
      showToast('Git 提交完成');
      requestGitStatus({ silent: true }).then(() => openGitPanel('status'));
    }).catch((error: any) => {
      if (statusEl) { statusEl.textContent = error.message || 'Git 提交失败'; statusEl.className = 'settings-status error'; }
      (submitBtn as HTMLButtonElement).disabled = false;
    });
  });
}

export function updateGitPanelBadge(): void {
  const badge = document.getElementById('git-badge');
  if (!badge) return;
  const summary = gitState.status?.summary;
  if (!summary) { badge.hidden = true; return; }
  const count = (summary.staged || 0) + (summary.modified || 0) + (summary.untracked || 0);
  if (count > 0) {
    badge.textContent = String(count > 99 ? '99+' : count);
    badge.hidden = false;
  } else {
    badge.hidden = true;
  }
}

// ============================================================
// Git modals
// ============================================================

function closeGitModal(): void {
  document.getElementById('git-modal-overlay')?.remove();
}

export function showGitTextModal(title: string, text: string, options: any = {}): void {
  closeGitModal();
  const { overlay, panel, close } = _createOverlayPanel!({
    overlayClass: 'modal-overlay',
    overlayId: 'git-modal-overlay',
    panelClass: 'modal-panel modal-panel-wide',
    maxWidth: '960px',
    panelHtml: `
      <div class="modal-header">
        <span class="modal-title">${escapeHtml(title)}</span>
        <button class="modal-close-btn" id="git-modal-close">✕</button>
      </div>
      <div class="modal-body">
        <pre style="margin:0;max-height:60vh;overflow:auto;background:#101828;color:#f8fafc;padding:16px;border-radius:8px;"><code class="${escapeHtml(options.language ? `language-${options.language}` : '')}">${escapeHtml(text || '暂无内容')}</code></pre>
      </div>
    `,
  });
  panel.querySelector('#git-modal-close')?.addEventListener('click', close);
  overlay.addEventListener('click', (event: MouseEvent) => { if (event.target === overlay) close(); });
  const codeEl = panel.querySelector('code');
  if ((window as any).hljs && codeEl && options.language) {
    try { (window as any).hljs.highlightElement(codeEl); } catch {}
  }
}

export async function showGitLogModal(): Promise<void> {
  try {
    const result = await sendGitCommand('log');
    const entries = Array.isArray(result.data?.entries) ? result.data.entries : [];
    gitState.logEntries = entries;
    gitState.panelLogEntries = entries;
    openGitPanel('log');
  } catch (error: any) {
    _appendError?.(error.message || '读取 Git 历史失败');
  }
}

export async function showGitDiffModal({ staged = false, file = '' } = {}): Promise<void> {
  try {
    const result = await sendGitCommand('diff', { staged, file });
    const title = file
      ? `Git Diff · ${file}${staged ? ' · 已暂存' : ''}`
      : (staged ? 'Git Diff · 已暂存' : 'Git Diff · 工作区');
    gitState.panelDiffContent = result.data?.diff || '当前范围没有 diff。';
    gitState.panelDiffTitle = title;
    openGitPanel(staged ? 'staged-diff' : 'diff');
  } catch (error: any) {
    _appendError?.(error.message || '读取 Git diff 失败');
  }
}

export async function performGitAdd(file = '.'): Promise<void> {
  if (file === '.') {
    const summary = gitState.status?.summary || {};
    const total = (summary.modified || 0) + (summary.untracked || 0) + (summary.staged || 0);
    const lines: string[] = [];
    if (summary.modified) lines.push(`${summary.modified} 个已修改`);
    if (summary.untracked) lines.push(`${summary.untracked} 个未跟踪`);
    if (summary.staged) lines.push(`${summary.staged} 个已在暂存区`);
    const desc = total > 0 ? lines.join('、') : '工作区无改动';
    const confirmed = await showGitConfirmModal({
      title: '全部暂存',
      message: `将暂存所有工作区改动。`,
      detail: desc,
      confirmLabel: '确认暂存',
    });
    if (!confirmed) return;
  }
  try {
    await sendGitCommand('add', { file });
    showToast(file === '.' ? '已暂存全部改动' : `已暂存 ${file}`);
    requestGitStatus({ silent: true });
  } catch (error: any) {
    _appendError?.(error.message || '暂存文件失败');
  }
}

export function showGitCommitModal(): void {
  openGitPanel('commit');
}

export function showGitConfirmModal({ title, message, detail, confirmLabel = '确认', danger = false, alertOnly = false }: any): Promise<boolean> {
  return new Promise((resolve) => {
    const { overlay, close } = _createOverlayPanel!({
      overlayClass: 'modal-overlay',
      panelClass: 'modal-panel',
      maxWidth: '400px',
      panelHtml: `
        <div class="modal-header">
          <span class="modal-title">${escapeHtml(title)}</span>
          <button class="modal-close-btn" id="git-confirm-close">✕</button>
        </div>
        <div class="modal-body" style="padding:16px 20px;display:flex;flex-direction:column;gap:10px">
          <p style="margin:0;color:var(--text-primary)">${escapeHtml(message)}</p>
          ${detail ? `<p style="margin:0;font-size:12px;color:var(--text-muted);font-family:var(--font-mono)">${escapeHtml(detail)}</p>` : ''}
          <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:4px">
            ${alertOnly ? '' : '<button class="modal-btn-secondary" id="git-confirm-cancel">取消</button>'}
            <button class="modal-btn-${danger ? 'danger' : 'primary'}" id="git-confirm-ok">${escapeHtml(confirmLabel)}</button>
          </div>
        </div>
      `,
    });
    const done = (val: boolean) => { close(); resolve(val); };
    overlay.querySelector('#git-confirm-close').addEventListener('click', () => done(false));
    if (!alertOnly) overlay.querySelector('#git-confirm-cancel').addEventListener('click', () => done(false));
    overlay.querySelector('#git-confirm-ok').addEventListener('click', () => done(true));
    overlay.addEventListener('click', (e: MouseEvent) => { if (e.target === overlay) done(false); });
  });
}

export async function showGitBranchPicker(): Promise<void> {
  try {
    const result = await sendGitCommand('branch');
    const branches = Array.isArray(result.data?.branches) ? result.data.branches : [];
    const current = result.data?.current || '';
    gitState.branchEntries = branches;
    if (branches.length === 0) {
      _appendError?.('未读取到任何 Git 分支');
      return;
    }
    _showOptionPicker!(
      '切换 Git 分支',
      branches.map((branch: any) => ({
        value: branch.name,
        label: branch.name,
        desc: branch.current ? '当前分支' : '点击后切换到该分支',
      })),
      current,
      async (value: string) => {
        if (value === current) {
          showToast(`当前已在 ${value}`);
          return;
        }
        const summary = gitState.status?.summary || {};
        const conflicted = summary.conflicted || 0;
        const dirty = (summary.staged || 0) + (summary.modified || 0) + (summary.untracked || 0);
        if (conflicted > 0) {
          await showGitConfirmModal({
            title: '存在冲突文件',
            message: `当前有 ${conflicted} 个冲突文件，请先解决冲突再切换分支。`,
            confirmLabel: '知道了',
            alertOnly: true,
          });
          return;
        }
        if (dirty > 0) {
          const lines: string[] = [];
          if (summary.staged) lines.push(`${summary.staged} 个已暂存`);
          if (summary.modified) lines.push(`${summary.modified} 个未暂存`);
          if (summary.untracked) lines.push(`${summary.untracked} 个未跟踪`);
          const confirmed = await showGitConfirmModal({
            title: '工作区有未提交改动',
            message: `切换到 ${value} 时，这些改动可能被一起带走或导致切换失败。`,
            detail: lines.join('、'),
            confirmLabel: '仍然切换',
            danger: true,
          });
          if (!confirmed) return;
        }
        try {
          await sendGitCommand('checkout', { branch: value });
          showToast(`已切换到 ${value}`);
          requestGitStatus({ silent: true });
        } catch (error: any) {
          _appendError?.(error.message || '切换分支失败');
        }
      }
    );
  } catch (error: any) {
    _appendError?.(error.message || '读取分支列表失败');
  }
}

export async function promptCreateGitBranch(): Promise<string | null> {
  return new Promise((resolve) => {
    const { overlay, close } = _createOverlayPanel!({
      overlayClass: 'modal-overlay',
      panelClass: 'modal-panel',
      maxWidth: '400px',
      panelHtml: `
        <div class="modal-header">
          <span class="modal-title">新建分支</span>
          <button class="modal-close-btn" id="git-branch-close">✕</button>
        </div>
        <div class="modal-body" style="padding:16px 20px;display:flex;flex-direction:column;gap:12px">
          <label class="modal-field-label" for="git-branch-name">分支名称</label>
          <input id="git-branch-name" type="text" placeholder="例如：feat/my-feature" autocomplete="off" style="width:100%;padding:8px 10px;background:var(--bg-secondary);border:1px solid var(--line);border-radius:2px;color:var(--text-primary);font-family:var(--font-mono);font-size:13px;box-sizing:border-box">
          <p style="margin:0;font-size:12px;color:var(--text-muted)">只创建分支，不会自动切换。</p>
          <div class="settings-status" id="git-branch-status"></div>
          <div style="display:flex;gap:8px;justify-content:flex-end">
            <button class="modal-btn-secondary" id="git-branch-cancel">取消</button>
            <button class="modal-btn-primary" id="git-branch-submit">创建分支</button>
          </div>
        </div>
      `,
    });
    const done = (val: string | null) => { close(); resolve(val); };
    const nameInput = overlay.querySelector('#git-branch-name') as HTMLInputElement;
    const statusEl = overlay.querySelector('#git-branch-status');
    const submitBtn = overlay.querySelector('#git-branch-submit') as HTMLButtonElement;
    overlay.querySelector('#git-branch-close').addEventListener('click', () => done(null));
    overlay.querySelector('#git-branch-cancel').addEventListener('click', () => done(null));
    overlay.addEventListener('click', (e: MouseEvent) => { if (e.target === overlay) done(null); });
    const doSubmit = async () => {
      const name = nameInput.value.trim();
      if (!name) {
        statusEl.textContent = '请输入分支名称';
        statusEl.className = 'settings-status error';
        nameInput.focus();
        return;
      }
      submitBtn.disabled = true;
      statusEl.textContent = '';
      try {
        await sendGitCommand('branch', { name });
        done(name);
        showToast(`已创建分支 ${name}`);
        requestGitStatus({ silent: true });
      } catch (error: any) {
        statusEl.textContent = error.message || '创建分支失败';
        statusEl.className = 'settings-status error';
        submitBtn.disabled = false;
      }
    };
    submitBtn.addEventListener('click', doSubmit);
    nameInput.addEventListener('keydown', (e: KeyboardEvent) => { if (e.key === 'Enter') doSubmit(); });
    setTimeout(() => nameInput.focus(), 50);
  });
}

// ============================================================
// Resize event listener setup (called from app.ts init)
// ============================================================

export function setupGitPanelResizeListeners(): void {
  const resizer = ensureGitPanelResizer();
  if (resizer) {
    resizer.addEventListener('pointerdown', handleGitPanelResizeStart);
    resizer.addEventListener('dblclick', () => applyGitPanelWidth(GIT_PANEL_DEFAULT_WIDTH));
    window.addEventListener('pointermove', handleGitPanelResizeMove);
    window.addEventListener('pointerup', handleGitPanelResizeEnd);
    window.addEventListener('pointercancel', handleGitPanelResizeEnd);
  }
}
